import React, { FC, Fragment, useRef, useState } from 'react';
import { FormControl, FormControlLabel, RadioGroup, Radio, TextField, FormHelperText } from 'drift-ui';
import { RootState } from 'app/configureStore';
import { useSelector } from 'react-redux';
import { DO_APPLICATION_TYPE, useSectionFormHelpers } from 'client-experience-toolbox';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import styles from './BaseFormStyles.scss';
import { useTranslation } from 'react-i18next';
import { binaryValueFormatter, combinedTwoStringRegex, companyNameRegex, emailRegex } from 'client-experience-toolbox';
import ContinueButton from '../../components/ContinueButton';
import { sendDOFirebaseEvent } from 'utils/utils';
import { useUpdateApplication } from 'utils/useUpdateApplication';

export const InsuranceInformation: FC = () => {
  const application = useSelector((state: RootState) => state?.applicationReducer?.factoringApplication);
  const insurance = application?.applicationData?.company?.insurance;
  const formRef = useRef<HTMLDivElement>(null);
  const [isPatching, setIsPatching] = useState(false);
  const { patchApplication, patchFullApplication } = useFormNetworkCalls(DO_APPLICATION_TYPE.FACTORING);
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.FACTORING);
  const {
    textHandlerGenerator,
    regexValidatorGenerator,
    presenceValidatorGenerator,
    binaryHandlerGenerator,
    clearValidation,
    handleInputFocus,
  } = useSectionFormHelpers({
    errorSetter: setErrors,
    patchApplication,
    updateApplication,
    application,
    fireBaseLogEvent: sendDOFirebaseEvent,
  });

  const insurancePath = 'applicationData.company.insurance';

  const fieldValidators = {
    provider: regexValidatorGenerator({
      key: `${insurancePath}.provider`,
      regex: companyNameRegex,
      errorText: t('invalid_insurance_provider'),
      skip: insurance?.hasInsurance !== true,
    }),
    contactName: regexValidatorGenerator({
      key: `${insurancePath}.contactName`,
      regex: combinedTwoStringRegex,
      errorText: t('first_name_error'),
      skip: insurance?.hasInsurance !== true,
    }),
    contactEmail: regexValidatorGenerator({
      key: `${insurancePath}.contactEmail`,
      regex: emailRegex,
      errorText: t('invalid_email'),
      makeLowerCase: true,
      skip: insurance?.hasInsurance !== true,
    }),
    hasInsurance: presenceValidatorGenerator({
      key: `${insurancePath}.hasInsurance`,
      errorText: t('invalid_option'),
    }),
  };

  const handleContinueClick = async () => {
    const isValid = await checkFormValidation();
    if (!isValid) {
      const firstErrorInput = formRef.current?.querySelector('.Mui-error') as HTMLElement;
      if (firstErrorInput) {
        firstErrorInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        firstErrorInput.focus({ preventScroll: true });
      }
    }
  };

  const checkFormValidation = async () => {
    const results = Object.values(fieldValidators).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);
    if (firstInvalid) {
      return false;
    } else {
      await patchFullApplication(setIsPatching);
      return true;
    }
  };

  return (
    <div ref={formRef} className={styles.formFieldContainer}>
      <div>
        <h2 className={styles.title}>{t('insurance_info.header_title')}</h2>
      </div>

      <Fragment>
        <div className={styles.groupedFields}>
          <div className={styles.questionContainer}>
            <p className={styles.questionTitle}>{t('insurance_info.insurance_question')}</p>
            <FormControl error={!!errors[`${insurancePath}.hasInsurance`]}>
              <RadioGroup
                value={binaryValueFormatter(insurance?.hasInsurance)}
                onChange={binaryHandlerGenerator(`${insurancePath}.hasInsurance`, clearValidation, [
                  insurancePath + '.provider',
                  insurancePath + '.contactName',
                  insurancePath + '.contactEmail',
                ])}
                onFocus={handleInputFocus}
              >
                <FormControlLabel value="yes" control={<Radio />} label={t('yes')} />
                <FormControlLabel value="no" control={<Radio />} label={t('no')} />
              </RadioGroup>
              {errors[`${insurancePath}.hasInsurance`] && (
                <FormHelperText>{errors[`${insurancePath}.hasInsurance`]}</FormHelperText>
              )}
            </FormControl>
          </div>
        </div>
        {insurance?.hasInsurance && (
          <>
            <div className={styles.groupedFields}>
              <TextField
                label={t('insurance_info.insurance_provider')}
                name="provider"
                value={insurance?.provider}
                onChange={textHandlerGenerator(`${insurancePath}.provider`, clearValidation)}
                onBlur={fieldValidators.provider}
                error={!!errors[`${insurancePath}.provider`]}
                onFocus={handleInputFocus}
                helperText={errors[`${insurancePath}.provider`] ?? ' '}
                slotProps={{ htmlInput: { maxLength: 50, autoCapitalize: 'sentences' } }}
              />
              <TextField
                label={t('insurance_info.contact_name')}
                name="contactName"
                onFocus={handleInputFocus}
                value={insurance?.contactName}
                onChange={textHandlerGenerator(`${insurancePath}.contactName`, clearValidation)}
                onBlur={fieldValidators.contactName}
                error={!!errors[`${insurancePath}.contactName`]}
                helperText={errors[`${insurancePath}.contactName`] ?? ' '}
                slotProps={{ htmlInput: { maxLength: 50, autoCapitalize: 'sentences' } }}
              />
            </div>
            <div className={styles.groupedFields}>
              <TextField
                className={styles.singleField}
                label={t('insurance_info.contact_email')}
                name="contactEmail"
                value={insurance?.contactEmail}
                onChange={textHandlerGenerator(`${insurancePath}.contactEmail`, clearValidation, true)}
                onBlur={fieldValidators.contactEmail}
                error={!!errors[`${insurancePath}.contactEmail`]}
                onFocus={handleInputFocus}
                helperText={errors[`${insurancePath}.contactEmail`] ?? ' '}
                slotProps={{ htmlInput: { autoCapitalize: 'none' } }}
              />
            </div>
          </>
        )}
        <div className={styles.footer}>
          <ContinueButton handleContinueClick={handleContinueClick} isPatching={isPatching} />
        </div>
      </Fragment>
    </div>
  );
};

export default InsuranceInformation;
